// import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building, Globe, Users, Award, Lightbulb, Factory, Shield, Star, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { emailUtils } from '../../utils/contactUtils';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import OptimizedImage from '../../components/Image/OptimizedImage';

const About = () => {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  
  // 轮播相关状态
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // 图片放大预览状态
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  
  // 检测是否为RTL语言
  const isRTL = i18n.language === 'ar';
  
  // 公司照片数据 - 可以轻松添加更多图片
  const companyPhotos = [
    { src: '/images/company/25e539787ecb6c0d7ce87b29dcdea23.jpg', alt: '研发现场' },
    { src: '/images/company/WX20250718-183637.png', alt: '办公环境' },
    { src: '/images/company/customer-service.jpg', alt: '客户服务' },
    { src: '/images/company/280c3cfcec5571194306b4bae30752.jpg', alt: '团队合作' },
    { src: '/images/company/0127c5413cd18d35215acdf6c3beb8.jpg', alt: '产品展示' },
    { src: '/images/company/5bab735c6e876f7d0af33de2e173db.jpg', alt: '技术交流' },
    { src: '/images/company/929175a0b71b1c41d7d524967ed2e1.jpg', alt: '工作场景' },
    { src: '/images/company/63d1af2d92c768ff4213d8066a923a.jpg', alt: '企业文化' },
    { src: '/images/company/9baa335c68ff447e8baf718f478ff8.jpg', alt: '团队建设' },
    { src: '/images/company/f1dbf5d955e3c92d3e1d40474d9ac2.jpg', alt: '业务洽谈' },
    { src: '/images/company/9f8cae8baeb67cd31ce7a44970c2b6.jpg', alt: '创新研发' }
  ];
  
  // 自动轮播
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => prev + 1);
    }, 4000); // 4秒自动切换
    
    return () => clearInterval(timer);
  }, [companyPhotos.length]);
  
  // 无缝循环处理
  useEffect(() => {
    // 当滚动到最后的复制图片时，瞬间跳回到原始位置
    if (currentSlide >= companyPhotos.length) {
      const timer = setTimeout(() => {
        setCurrentSlide(0);
      }, 500); // 等待动画完成后跳转
      return () => clearTimeout(timer);
    }
    // 当滚动到最前面的复制图片时，瞬间跳到末尾位置
    if (currentSlide < 0) {
      const timer = setTimeout(() => {
        setCurrentSlide(companyPhotos.length - 1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentSlide, companyPhotos.length]);
  
  // 键盘事件监听 - ESC关闭图片预览，左右键切换图片
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedImageIndex(null);
      } else if (selectedImageIndex !== null) {
        if (event.key === 'ArrowLeft') {
          isRTL ? nextImage() : prevImage();
        } else if (event.key === 'ArrowRight') {
          isRTL ? prevImage() : nextImage();
        }
      }
    };
    
    if (selectedImageIndex !== null) {
      document.addEventListener('keydown', handleKeyDown);
      // 防止背景滚动
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [selectedImageIndex, isRTL]);
  
  // 手动切换
  const nextSlide = () => {
    setCurrentSlide((prev) => prev + 1);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => prev - 1);
  };
  
  // 打开图片预览 - 传入图片索引
  const openImagePreview = (index: number) => {
    setSelectedImageIndex(index);
  };
  
  // 关闭图片预览
  const closeImagePreview = () => {
    setSelectedImageIndex(null);
  };
  
  // 预览模态框中的图片切换
  const nextImage = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex + 1) % companyPhotos.length);
    }
  };
  
  const prevImage = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex - 1 + companyPhotos.length) % companyPhotos.length);
    }
  };
  
  return (
    <div className="pt-24 bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('pages.about.hero.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t('pages.about.hero.subtitle')}
          </p>
          <div className="text-lg text-blue-600 font-semibold">
            {t('pages.about.hero.motto')}
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('pages.about.overview.title')}
              </h2>
              <div className="prose prose-lg text-gray-600">
                <p className="mb-4">
                  {t('pages.about.overview.description1')}
                </p>
                <p className="mb-4">
                  {t('pages.about.overview.description2')}
                </p>
                <p>
                  {t('pages.about.overview.description3')}
                </p>
              </div>
            </div>
            <div className="bg-blue-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('pages.about.achievements.title')}</h3>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">10+</div>
                  <div className="text-sm text-gray-600">{t('pages.about.achievements.experience')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">32</div>
                  <div className="text-sm text-gray-600">{t('pages.about.achievements.provinces')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">3389+</div>
                  <div className="text-sm text-gray-600">{t('pages.about.achievements.libraries')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">2.3M+</div>
                  <div className="text-sm text-gray-600">{t('pages.about.achievements.readers')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Strengths */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('pages.about.strengths.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('pages.about.strengths.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <Lightbulb className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.about.strengths.rd.title')}</h3>
              <p className="text-gray-600">
                {t('pages.about.strengths.rd.description')}
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <Factory className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.about.strengths.manufacturing.title')}</h3>
              <p className="text-gray-600">
                {t('pages.about.strengths.manufacturing.description')}
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.about.strengths.quality.title')}</h3>
              <p className="text-gray-600">
                {t('pages.about.strengths.quality.description')}
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <Globe className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.about.strengths.solutions.title')}</h3>
              <p className="text-gray-600">
                {t('pages.about.strengths.solutions.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications and Certifications */}
      {/* 单独展示的资质图片 */}
      <div className="w-full flex justify-center mb-10">
        <img
          src="/images/company/WX20250718-194600.png"
          alt="公司资质证书"
          style={{ maxWidth: '1220px', width: '100%', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
        />
      </div>
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('pages.about.certifications.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('pages.about.certifications.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-blue-50 p-6 rounded-lg text-center">
              <Award className="h-10 w-10 text-blue-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900">{t('pages.about.certifications.highTech.title')}</h3>
              <p className="text-sm text-gray-600 mt-2">{t('pages.about.certifications.highTech.description')}</p>
            </div>
            
            <div className="bg-green-50 p-6 rounded-lg text-center">
              <Star className="h-10 w-10 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900">{t('pages.about.certifications.patents.title')}</h3>
              <p className="text-sm text-gray-600 mt-2">{t('pages.about.certifications.patents.description')}</p>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-lg text-center">
              <Shield className="h-10 w-10 text-purple-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900">{t('pages.about.certifications.qualitySystem.title')}</h3>
              <p className="text-sm text-gray-600 mt-2">{t('pages.about.certifications.qualitySystem.description')}</p>
            </div>
            
            <div className="bg-orange-50 p-6 rounded-lg text-center">
              <Building className="h-10 w-10 text-orange-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900">{t('pages.about.certifications.copyrights.title')}</h3>
              <p className="text-sm text-gray-600 mt-2">{t('pages.about.certifications.copyrights.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Focus */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('pages.about.technology.title')}
              </h2>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.about.technology.software.title')}</h3>
                  <p className="text-gray-600">
                    {t('pages.about.technology.software.description')}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.about.technology.hardware.title')}</h3>
                  <p className="text-gray-600">
                    {t('pages.about.technology.hardware.description')}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.about.technology.rfid.title')}</h3>
                  <p className="text-gray-600">
                    {t('pages.about.technology.rfid.description')}
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {t('pages.about.customerStories.title')}
              </h2>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-gray-900">{t('pages.about.customerStories.university.title')}</h3>
                  <p className="text-gray-600 mt-2">
                    {t('pages.about.customerStories.university.description')}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-gray-900">{t('pages.about.customerStories.healthcare.title')}</h3>
                  <p className="text-gray-600 mt-2">
                    {t('pages.about.customerStories.healthcare.description')}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-gray-900">{t('pages.about.customerStories.corporate.title')}</h3>
                  <p className="text-gray-600 mt-2">
                    {t('pages.about.customerStories.corporate.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Partnership Invitation */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            {t('pages.about.partnership.title')}
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            {t('pages.about.partnership.subtitle')}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-blue-700 p-6 rounded-lg">
              <Users className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('pages.about.partnership.types.integrators.title')}</h3>
              <p className="text-blue-100">
                {t('pages.about.partnership.types.integrators.description')}
              </p>
            </div>
            
            <div className="bg-blue-700 p-6 rounded-lg">
              <Globe className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('pages.about.partnership.types.distributors.title')}</h3>
              <p className="text-blue-100">
                {t('pages.about.partnership.types.distributors.description')}
              </p>
            </div>
            
            <div className="bg-blue-700 p-6 rounded-lg">
              <Building className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('pages.about.partnership.types.institutional.title')}</h3>
              <p className="text-blue-100">
                {t('pages.about.partnership.types.institutional.description')}
              </p>
            </div>
          </div>
          
          <div className="bg-white text-gray-900 p-8 rounded-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">{t('pages.about.partnership.benefits.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div>
                <h4 className="font-semibold text-blue-600 mb-2">{t('pages.about.partnership.benefits.track.title')}</h4>
                <p className="text-gray-600">
                  {t('pages.about.partnership.benefits.track.description')}
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-blue-600 mb-2">{t('pages.about.partnership.benefits.manufacturing.title')}</h4>
                <p className="text-gray-600">
                  {t('pages.about.partnership.benefits.manufacturing.description')}
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-blue-600 mb-2">{t('pages.about.partnership.benefits.support.title')}</h4>
                <p className="text-gray-600">
                  {t('pages.about.partnership.benefits.support.description')}
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-blue-600 mb-2">{t('pages.about.partnership.benefits.innovation.title')}</h4>
                <p className="text-gray-600">
                  {t('pages.about.partnership.benefits.innovation.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Photos Carousel Section - 公司工作掠影 */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-blue-700 mb-4">
              {t('common.companyPhotos.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('common.companyPhotos.description')}
            </p>
          </motion.div>
          
          {/* 轮播容器 */}
          <div className={`relative ${isRTL ? 'rtl' : 'ltr'}`}>
            {/* 左右控制按钮 */}
            <button
              onClick={isRTL ? nextSlide : prevSlide}
              className={`absolute ${isRTL ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-200 hover:scale-110`}
            >
              <ChevronLeft className={`w-6 h-6 text-blue-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            <button
              onClick={isRTL ? prevSlide : nextSlide}
              className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-200 hover:scale-110`}
            >
              <ChevronRight className={`w-6 h-6 text-blue-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            
            {/* 图片展示区域 */}
            <div className="overflow-hidden rounded-xl">
              <motion.div
                className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
                animate={{ 
                  x: isRTL 
                    ? `${(currentSlide + companyPhotos.length) * (100 / 3)}%`
                    : `${-(currentSlide + companyPhotos.length) * (100 / 3)}%`
                }}
                transition={{ 
                  duration: currentSlide < 0 || currentSlide >= companyPhotos.length ? 0 : 0.5, 
                  ease: "easeInOut" 
                }}
              >
                {/* 前面的复制图片 - 用于无缝循环 */}
                {companyPhotos.map((photo, index) => (
                  <motion.div
                    key={`prev-${index}`}
                    className="flex-shrink-0 w-1/3 px-3"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div
                      className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg bg-white hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                      onClick={() => openImagePreview(index)}
                    >
                      <OptimizedImage 
                        src={photo.src} 
                        alt={photo.alt} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                      />
                      {/* 放大图标提示 */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
                          <ZoomIn className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {/* 原始图片 */}
                {companyPhotos.map((photo, index) => (
                  <motion.div
                    key={index}
                    className="flex-shrink-0 w-1/3 px-3"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div
                      className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg bg-white hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                      onClick={() => openImagePreview(index)}
                    >
                      <OptimizedImage 
                        src={photo.src} 
                        alt={photo.alt} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                      />
                      {/* 放大图标提示 */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
                          <ZoomIn className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {/* 后面的复制图片 - 用于无缝循环 */}
                {companyPhotos.map((photo, index) => (
                  <motion.div
                    key={`next-${index}`}
                    className="flex-shrink-0 w-1/3 px-3"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div
                      className="aspect-[4/3] rounded-xl overflow-hidden shadow-lg bg-white hover:shadow-xl transition-all duration-300 cursor-pointer group relative"
                      onClick={() => openImagePreview(index)}
                    >
                      <OptimizedImage 
                        src={photo.src} 
                        alt={photo.alt} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                      />
                      {/* 放大图标提示 */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
                          <ZoomIn className="w-5 h-5 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
            
            {/* 指示器 */}
            <div className="flex justify-center mt-6 space-x-2">
              {companyPhotos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    (currentSlide % companyPhotos.length) === index 
                      ? 'bg-blue-600 scale-110' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 图片预览模态框 */}
      {selectedImageIndex !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={closeImagePreview}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="relative max-w-5xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 关闭按钮 */}
            <button
              onClick={closeImagePreview}
              className={`absolute -top-4 ${isRTL ? '-left-4' : '-right-4'} z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors`}
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
            
            {/* 放大的图片 */}
            <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
              <OptimizedImage
                src={companyPhotos[selectedImageIndex].src}
                alt={companyPhotos[selectedImageIndex].alt}
                className="w-full h-full object-contain max-h-[80vh]"
              />
            </div>
            
            {/* 左右切换按钮 */}
            <button
              onClick={isRTL ? nextImage : prevImage}
              className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-200 hover:scale-110`}
            >
              <ChevronLeft className={`w-6 h-6 text-blue-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            <button
              onClick={isRTL ? prevImage : nextImage}
              className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-200 hover:scale-110`}
            >
              <ChevronRight className={`w-6 h-6 text-blue-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>

            {/* 当前图片索引指示器 */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {selectedImageIndex + 1} / {companyPhotos.length}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Contact Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            {t('pages.about.contact.title')}
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            {t('pages.about.contact.subtitle')}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">{t('pages.about.contact.headquarters.title')}</h3>
              <p className="text-gray-600 whitespace-pre-line">
                {t('pages.about.contact.headquarters.address')}
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">{t('pages.about.contact.contactInfo.title')}</h3>
              <div className="text-gray-600 space-y-2">
                <p>{t('pages.about.contact.contactInfo.phone')}</p>
                <p>
                  Email: 
                  <button 
                    onClick={() => emailUtils.openDirectEmail('rfidlib@libmj.com')}
                    className="ml-1 text-blue-600 hover:text-blue-800 underline cursor-pointer transition-colors duration-200"
                    title="发送邮件到 rfidlib@libmj.com"
                  >
                    rfidlib@libmj.com
                  </button>
                </p>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">{t('pages.about.contact.globalReach.title')}</h3>
              <p className="text-gray-600 whitespace-pre-line">
                {t('pages.about.contact.globalReach.description')}
              </p>
            </div>
          </div>
          
          <div className="mt-12">
            <button 
              className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              onClick={() => emailUtils.openConsultationEmail(t)}
            >
              {t('pages.about.contact.cta')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 